import { useEffect, useRef } from 'react';
import { useAuth } from '@/context/AuthContext';

export function useLocationTracking(enabled = true, intervalMs = 60000) {
  const { api, user } = useAuth();
  const watchIdRef = useRef(null);
  const intervalRef = useRef(null);
  const lastPositionRef = useRef(null);

  const sendLocation = async (position) => {
    if (!user || !position) return;
    
    try {
      await api().post('/location/update', {
        lat: position.coords.latitude,
        lng: position.coords.longitude,
        accuracy: position.coords.accuracy,
        speed: position.coords.speed,
        heading: position.coords.heading
      });
      lastPositionRef.current = position;
    } catch (error) {
      console.error('Error sending location:', error);
    }
  };

  useEffect(() => {
    if (!enabled || !user || !navigator.geolocation) return;

    // Get initial position
    navigator.geolocation.getCurrentPosition(
      sendLocation,
      (error) => console.log('Location error:', error),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );

    // Watch position for significant changes
    watchIdRef.current = navigator.geolocation.watchPosition(
      (position) => {
        // Only send if moved significantly (>50m) or speed changed
        const last = lastPositionRef.current;
        if (!last) {
          sendLocation(position);
          return;
        }
        
        const distance = calculateDistance(
          last.coords.latitude, last.coords.longitude,
          position.coords.latitude, position.coords.longitude
        );
        
        if (distance > 0.05) { // 50 meters
          sendLocation(position);
        }
      },
      (error) => console.log('Watch error:', error),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 30000 }
    );

    // Also send periodically even if not moving
    intervalRef.current = setInterval(() => {
      navigator.geolocation.getCurrentPosition(
        sendLocation,
        () => {},
        { enableHighAccuracy: false, timeout: 5000, maximumAge: 60000 }
      );
    }, intervalMs);

    return () => {
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
      }
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [enabled, user]);
}

// Haversine formula to calculate distance in km
function calculateDistance(lat1, lon1, lat2, lon2) {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
            Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
            Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c;
}

export default useLocationTracking;
